import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useState } from 'react';
import { tryApi } from 'utils/api/common';
import { giftCardIntegrationApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import styles from './AccessGiftCardButton.module.css';

interface IAccessGiftCardButtonProps {
  prizeOrderProductId: string;
}

const AccessGiftCardButton: React.FC<IAccessGiftCardButtonProps> = ({
  prizeOrderProductId,
}) => {
  const { displayToast } = useUIStore();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);

    // We have to load the Gift Card URL then open a popup. If we do this the normal way, the popup won't open, because
    // the browser no longer trusts the user's click after the await. Therefore, we must open the popup first, populate
    // it with a 'please wait' message, then update the location of the URL once the response is received
    let popupWindow: Window | null | undefined;
    try {
      popupWindow = window.open('about:blank', '_blank');
      if (popupWindow) {
        popupWindow.document.head.innerHTML =
          '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">';
        popupWindow.document.body.innerHTML =
          '<p style="text-align: center;width:100%;height: 100vh;display:flex;align-items:center;justify-content:center">Please wait while we retrieve your gift card...</p>';
      }
    } catch (e) {
      console.log(
        'Failed to initialise popup window for AccessGiftCardButton, this will fallback to non-popup implementation',
      );
      if (popupWindow) {
        // If the popup did open, but its content failed to get set, then attempt to close the popup
        try {
          popupWindow.close();
        } catch (e) {
          console.log(
            'Attempted to close a popup window for AccessGiftCardButton, this will fallback to non-popup implementation',
          );
        }
        // Make sure we set the popupWindow variable back to null, so the fallback code runs
        popupWindow = null;
      }
    }
    const resp = await tryApi(
      giftCardIntegrationApiClient.auth.getGiftCardRedeemUrl({
        query: { prizeOrderProductId },
      }),
    );
    setLoading(false);

    if (resp.status === HttpStatusCode.OK) {
      // It is possible that the window.open still failed. In that scenario, just send the user to the gift card in the
      // current window
      if (!popupWindow) {
        window.location.href = resp.body;
      } else {
        popupWindow.location.href = resp.body;
      }
    } else {
      popupWindow?.close();
      displayToast({
        title: 'Oops, something went wrong while retrieving your gift card.',
        color: '#FF6600',
        timeout: 5000,
      });

      if (resp.status === 'error') {
        throw resp.error;
      }
    }
  };

  return (
    <RoundedButton
      colorStyle="OutlineDark"
      className={styles.accessGiftCardButton}
      key={prizeOrderProductId}
      onClick={handleClick}
      loading={loading}
    >
      Access Gift Card
    </RoundedButton>
  );
};

export default AccessGiftCardButton;
